<script setup>
import {
  LMap,
  LTileLayer,
  LControlZoom,
  LImageOverlay,
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useCourseOutlinesView } from "@/scripts/Utilities/CourseOutlinesView";
import SearchableSelect from "@/components/SearchableSelectComponent.vue";
import { ref } from "vue";

const {
  courses,
  targetCourse,
  loading,
  zoom,
  center,
  url,
  opacity,
  overlay,
  mapImage,
  bounds,
  showCourseMap,
  mapRotation,
  isEditing,
  hasOutline,
  enableSendToDevice,
  outlinesEdited,
  ready,
  changeView,
  startCreatingOutline,
  startEditingOutline,
  stopEditing,
  deleteOutline,
  saveCourseOutline,
  applyBoundsAndOverlays,
  sendConfig,
} = useCourseOutlinesView();

const mapInstance = ref(null);

const onMapReady = () => {
  if (mapInstance.value && mapInstance.value.leafletObject) {
    ready(mapInstance.value.leafletObject);
  }
};
</script>

<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">Course Outlines:</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item"><a href="#">Dashboard</a></li>
              <li class="breadcrumb-item active">Devices</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="card" id="course-config">
          <div class="card-body">
            <div class="box-body table-responsive no-padding">
              <div class="row">
                <div class="col-9">
                  <LMap
                    style="height: 650px; border-radius: 5px"
                    ref="mapInstance"
                    @ready="onMapReady"
                    :zoom="zoom"
                    :zoomAnimation="false"
                    :center="center"
                    :options="{
                      zoomControl: false,
                      attributionControl: false,
                      maxBounds: bounds.every((coord) =>
                        coord.every((val) => val !== 0)
                      )
                        ? bounds
                        : undefined,
                    }"
                  >
                    <LTileLayer :zoomAnimation="false" :url="url"></LTileLayer>

                    <LImageOverlay
                      v-if="overlay && showCourseMap && bounds[0][0] !== 0"
                      :opacity="opacity"
                      :url="overlay"
                      :bounds="bounds"
                      :z-index="10"
                      :interactive="false"
                      :zoomAnimation="false"
                      :style="{ transform: `rotate(${mapRotation}deg)` }"
                    ></LImageOverlay>

                    <LImageOverlay
                      v-if="mapImage && showCourseMap && bounds[0][0] !== 0"
                      :opacity="opacity"
                      :url="mapImage"
                      :bounds="bounds"
                      :interactive="false"
                      :zoomAnimation="false"
                      :style="{ transform: `rotate(${mapRotation}deg)` }"
                    ></LImageOverlay>

                    <LControlZoom
                      :zoomAnimation="false"
                      position="topright"
                    ></LControlZoom>
                  </LMap>
                </div>
                <div class="col-3">
                  <div class="col-12">
                    <div class="form-group">
                      <label>Select course:</label>
                      <SearchableSelect
                        v-model="targetCourse"
                        :options="courses"
                        :label="'name'"
                        :trackBy="'id'"
                        :placeholder="'Select the courses'"
                      />
                    </div>
                  </div>

                  <div v-if="targetCourse" class="col-12 text-center">
                    <div class="btn-group">
                      <button
                        @click="changeView(false)"
                        type="button"
                        class="btn btn-sm"
                        :class="{
                          'btn-primary': showCourseMap,
                          'btn-default': !showCourseMap,
                        }"
                      >
                        TM Map
                      </button>
                      <button
                        @click="changeView(true)"
                        type="button"
                        class="btn btn-sm"
                        :class="{
                          'btn-primary': !showCourseMap,
                          'btn-default': showCourseMap,
                        }"
                      >
                        Satellite
                      </button>
                    </div>
                    <hr />
                  </div>

                  <div
                    v-if="targetCourse"
                    class="col-12 d-flex flex-column gap-2"
                  >
                    <div v-if="!isEditing" class="d-grid gap-2">
                      <button
                        v-if="hasOutline"
                        @click="startEditingOutline"
                        class="btn btn-warning btn-block"
                      >
                        {{
                          outlinesEdited ? "Continue Editing" : "Edit Outline"
                        }}
                      </button>
                      <button
                        v-else
                        @click="startCreatingOutline"
                        class="btn btn-primary btn-block"
                      >
                        Create Outline
                      </button>
                      <button
                        v-if="hasOutline"
                        @click="deleteOutline"
                        class="btn btn-danger btn-block"
                      >
                        Delete Outline
                      </button>
                    </div>

                    <div v-else class="d-grid gap-2">
                      <div class="alert alert-info p-2">
                        <p class="mb-0"><strong>Drawing Mode Active</strong></p>
                        <small>
                          - Drag the points on the map to draw the outline
                          <br />
                          - Clicking a white point(coordinate) removes a
                          coordinate
                          <br />
                          - Clicking a grey point(coordinate) adds a coordinate
                        </small>
                      </div>
                      <button
                        @click="stopEditing"
                        class="btn btn-warning btn-block"
                      >
                        View Edit
                      </button>
                    </div>
                    <button
                      v-if="outlinesEdited"
                      @click="saveCourseOutline"
                      class="btn btn-success btn-block"
                    >
                      Save Outline
                    </button>

                    <button
                      class="btn btn-info mt-2"
                      @click="applyBoundsAndOverlays"
                      v-if="targetCourse"
                    >
                      Reset Map View
                    </button>

                    <div v-if="targetCourse" class="form-group row">
                      <button
                        class="btn m-2"
                        @click="sendConfig"
                        :disabled="!enableSendToDevice"
                        :class="{
                          'btn-info': enableSendToDevice,
                          'btn-secondary': !enableSendToDevice,
                        }"
                        :style="{
                          width: '100%',
                          cursor: enableSendToDevice
                            ? 'pointer'
                            : 'not-allowed',
                        }"
                      >
                        Send to devices
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="overlay" v-show="loading">
            <i class="fas fa-spinner fa-spin fa-lg"></i>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.overlay {
  z-index: 1000 !important;
  background-color: rgba(27, 30, 33, 0.7) !important;
}

.overlay i {
  color: white !important;
}

.import {
  border-radius: 0 3px 3px 0 !important;
}

/* Hide any Leaflet attribution */
:deep(.leaflet-control-attribution) {
  display: none !important;
}

.gap-2 {
  gap: 0.5rem;
}

.d-grid {
  display: grid;
}
</style>
